import { supabase } from '../config/supabase';

export const projectService = {
  // Get all projects for current user
  async getProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:clients(id, name, company_name),
        files(id, filename, file_type, file_size),
        folders(
          id,
          name,
          folder_type,
          files(id, filename, file_type),
          door_metadata(*)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get single project with details
  async getProject(id) {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        client:clients(*),
        files(*),
        folders(
          *,
          files(*),
          door_metadata(*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Create new project
  async createProject(projectData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('projects')
      .insert([{
        project_number: projectData.projectNumber,
        name: projectData.name,
        description: projectData.description,
        status: projectData.status || 'Draft',
        due_date: projectData.dueDate,
        client_id: projectData.clientId || null,
        created_by: user.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update project
  async updateProject(id, updates) {
    const updateData = {
      name: updates.name,
      description: updates.description,
      status: updates.status
    };

    // Only include fields that are provided
    if (updates.projectNumber) updateData.project_number = updates.projectNumber;
    if (updates.dueDate) updateData.due_date = updates.dueDate;
    if (updates.clientId !== undefined) updateData.client_id = updates.clientId;

    console.log('ProjectService - Updating project:', id);
    console.log('ProjectService - Update data:', updateData);

    const { data, error } = await supabase
      .from('projects')
      .update(updateData)
      .eq('id', id)
      .select('*, client:clients(*)');

    console.log('ProjectService - Response data:', data);
    console.log('ProjectService - Response error:', error);

    if (error) throw error;
    return data && data.length > 0 ? data[0] : data;
  },

  // Delete project
  async deleteProject(id) {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Check if project number exists
  async checkProjectNumber(projectNumber, excludeId = null) {
    let query = supabase
      .from('projects')
      .select('id')
      .eq('project_number', projectNumber);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data.length > 0;
  },

  // Copy project with all folders, door metadata, and file links
  async copyProject(sourceProjectId, newProjectNumber) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // 1. Get the source project with all its data
    const { data: sourceProject, error: fetchError } = await supabase
      .from('projects')
      .select(`
        *,
        folders(
          *,
          door_metadata(*),
          files(*)
        ),
        files(*)
      `)
      .eq('id', sourceProjectId)
      .single();

    if (fetchError) throw fetchError;

    // 2. Create the new project
    const { data: newProject, error: projectError } = await supabase
      .from('projects')
      .insert([{
        project_number: newProjectNumber,
        name: sourceProject.name + ' (Copy)',
        description: sourceProject.description,
        status: 'Draft',
        due_date: sourceProject.due_date,
        client_id: sourceProject.client_id,
        created_by: user.id
      }])
      .select()
      .single();

    if (projectError) throw projectError;

    // 3. Copy project-level files (link to new project)
    if (sourceProject.files && sourceProject.files.length > 0) {
      const projectFilesToCopy = sourceProject.files
        .filter(file => !file.folder_id) // Only project-level files
        .map(file => ({
          filename: file.filename,
          original_name: file.original_name,
          file_type: file.file_type,
          file_path: file.file_path,
          file_size: file.file_size,
          project_id: newProject.id,
          folder_id: null,
          uploaded_by: user.id
        }));

      if (projectFilesToCopy.length > 0) {
        const { error: filesError } = await supabase
          .from('files')
          .insert(projectFilesToCopy);

        if (filesError) throw filesError;
      }
    }

    // 4. Copy folders with their door metadata and files
    if (sourceProject.folders && sourceProject.folders.length > 0) {
      for (const folder of sourceProject.folders) {
        // Create new folder
        const { data: newFolder, error: folderError } = await supabase
          .from('folders')
          .insert([{
            name: folder.name,
            folder_type: folder.folder_type,
            project_id: newProject.id,
            created_by: user.id
          }])
          .select()
          .single();

        if (folderError) throw folderError;

        // Copy door metadata if exists
        if (folder.door_metadata && folder.door_metadata.length > 0) {
          const doorMetadata = folder.door_metadata[0];
          const { error: metadataError } = await supabase
            .from('door_metadata')
            .insert([{
              folder_id: newFolder.id,
              width: doorMetadata.width,
              height: doorMetadata.height,
              door_name: doorMetadata.door_name,
              has_glass: doorMetadata.has_glass,
              hinge_type: doorMetadata.hinge_type,
              lock_type: doorMetadata.lock_type,
              striker_type: doorMetadata.striker_type,
              frame_paint: doorMetadata.frame_paint,
              amount: doorMetadata.amount,
              waternose: doorMetadata.waternose,
              threshold_type: doorMetadata.threshold_type,
              kickplate: doorMetadata.kickplate,
              surface_finish: doorMetadata.surface_finish,
              custom_finish: doorMetadata.custom_finish,
              color_code: doorMetadata.color_code,
              handedness: doorMetadata.handedness,
              glass_style: doorMetadata.glass_style,
              glass_width: doorMetadata.glass_width,
              glass_height: doorMetadata.glass_height,
              groove_design: doorMetadata.groove_design,
              hinge_count: doorMetadata.hinge_count,
              door_status: doorMetadata.door_status,
              special_feature: doorMetadata.special_feature,
              item_nr: doorMetadata.item_nr,
              barcode_url: doorMetadata.barcode_url
            }]);

          if (metadataError) throw metadataError;
        }

        // Copy folder files (link to new project and folder)
        if (folder.files && folder.files.length > 0) {
          const folderFilesToCopy = folder.files.map(file => ({
            filename: file.filename,
            original_name: file.original_name,
            file_type: file.file_type,
            file_path: file.file_path,
            file_size: file.file_size,
            project_id: newProject.id,
            folder_id: newFolder.id,
            uploaded_by: user.id
          }));

          const { error: folderFilesError } = await supabase
            .from('files')
            .insert(folderFilesToCopy);

          if (folderFilesError) throw folderFilesError;
        }
      }
    }

    return newProject;
  }
};

export default projectService;
